import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Eye, EyeOff, Mail, Lock, User, Home, Phone, Banknote, Store, Image,
  ArrowLeft, ArrowRight, ChevronDown, CheckCircle, Loader2, Info, XCircle
} from "lucide-react";
import BankSelect from './components/BankSelect';
import { BASE_URL } from '../../apiConfig';
import { toast } from "sonner";

const InputField = ({ id, label, type = "text", value, onChange, icon: Icon, placeholder, required, children, error, className = "", ...props }) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-xs font-semibold text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />}
      {children || (
        <input
          type={type}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          className={`w-full ${Icon ? 'pr-11' : 'pr-4'} pl-4 py-3 border ${error ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none ${className}`}
          placeholder={placeholder}
          required={required}
          {...props}
        />
      )}
    </div>
    {error && (
      <p className="text-red-500 text-xs mt-1 flex items-center">
        <Info className="w-3 h-3 ml-1 inline-block text-xs" /> {error}
      </p>
    )}
  </div>
);

export default function RegisterPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    nameStore: "",
    nameBank: "",
    rib: "",
    address: "",
    phone: "",
    level: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  const handleLogoChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogo(null);
      setLogoPreview(null);
    }
    setErrors(prev => ({ ...prev, logo: undefined }));
  }, []);

  const clearLogo = useCallback(() => {
    setLogo(null);
    setLogoPreview(null);
    setErrors(prev => ({ ...prev, logo: undefined }));
  }, []);

  const validateStep = useCallback((step) => {
    let newErrors = {};
    
    if (step === 1) {
      if (!formData.name) newErrors.name = "الاسم الكامل مطلوب";
      if (!formData.email) {
        newErrors.email = "البريد الإلكتروني مطلوب.";
      } else if (!/\S+.\S+\.\S+/.test(formData.email)) {
        newErrors.email = "صيغة البريد الإلكتروني غير صالحة.";
      }
      if (!formData.password) {
        newErrors.password = "كلمة المرور مطلوبة.";
      } else if (formData.password.length < 6) {
        newErrors.password = "كلمة المرور يجب أن تتكون من 6 أحرف على الأقل.";
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "تأكيد كلمة المرور مطلوب.";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "كلمة المرور وتأكيدها غير متطابقين.";
      }
    } else if (step === 2) {
      if (!formData.nameBank) newErrors.nameBank = "اسم البنك مطلوب.";
      if (!formData.rib) newErrors.rib = "رقم الحساب البنكي (RIB) مطلوب.";
      if (!formData.address) newErrors.address = "العنوان مطلوب.";
      if (!formData.phone) newErrors.phone = "رقم الهاتف مطلوب.";
      if (!formData.level) newErrors.level = "مستوى البائع مطلوب.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleNextStep = () => {
    const isValid = validateStep(currentStep);
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError("");
    setSuccessMessage("");

    const isStep2Valid = validateStep(2);
    if (!isStep2Valid) {
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("password", formData.password);
      formDataToSend.append("password_confirmation", formData.confirmPassword);
      formDataToSend.append("role", 0);
      formDataToSend.append("name_store", formData.nameStore);
      formDataToSend.append("name_bank", formData.nameBank);
      formDataToSend.append("rib", formData.rib);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("level", formData.level);
      if (logo) {
        formDataToSend.append("logo", logo);
      }

      const response = await fetch(BASE_URL + "/api/register", {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          setErrors(data.errors);
          const hasStep1Errors = Object.keys(data.errors).some(key => 
            ['name', 'email', 'password', 'confirmPassword'].includes(key)
          );
          if (hasStep1Errors) {
            setCurrentStep(1);
          }
          setGeneralError("الرجاء تصحيح أخطاء التسجيل");
        } else {
          setGeneralError(data.message || "فشل في إنشاء الحساب الأساسي.");
        }
        return;
      }

      toast.success("تم إنشاء حسابك بنجاح!");
      setSuccessMessage("تم إنشاء حسابك ومعلومات البائع بنجاح! سيتم تحويلك إلى صفحة تسجيل الدخول");
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    } catch (err) {
      console.error("Registration error:", err);
      setGeneralError("فشل الاتصال بالخادم. الرجاء المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-br from-[var(--color-primary-50)] via-white to-[var(--color-primary-50)] flex items-center justify-center p-4 font-cairo">
      <div className="w-full max-w-xl">
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
          <div className="text-center mb-3">
            <div className="mx-auto w-12 h-12 bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">تسجيل حساب جديد</h1>
            <p className="text-gray-600 text-xs">
              الخطوة {currentStep} من 2:
              {currentStep === 1 ? " معلومات الحساب الأساسية" : " تفاصيل معلومات البائع"}
            </p>
          </div>

          <div className="relative pt-1">
            <div className="overflow-hidden h-1.5 mb-4 text-xs flex rounded bg-[var(--color-primary-100)]">
              <div style={{ width: currentStep === 1 ? "50%" : "100%" }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[var(--color-primary-500)] transition-all duration-300 ease-in-out"></div>
            </div>
          </div>

          <form onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }} className="space-y-6">
            {generalError && (
              <div className="text-red-600 bg-red-100 border border-red-300 px-4 py-1.5 rounded-lg text-xs text-center">
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="inline-block ml-2 w-4 h-4" />
                  {generalError}
                </div>
              </div>
            )}
            
            {successMessage && (
              <div className="text-green-600 bg-green-100 border border-green-300 px-4 py-2 rounded-lg text-sm text-center flex items-center justify-center">
                <CheckCircle className="inline-block ml-2 w-4 h-4" />
                {successMessage}
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">معلومات الحساب</h2>
                <InputField
                  id="name"
                  label="الاسم الكامل"
                  icon={User}
                  value={formData.name}
                  name="name"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder="أدخل اسمك الكامل"
                  required
                  error={errors.name}
                />

                <InputField
                  id="email"
                  label="البريد الإلكتروني"
                  type="email"
                  icon={Mail}
                  value={formData.email}
                  name="email"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder="أدخل بريدك الإلكتروني"
                  required
                  error={errors.email}
                />

                <div className="flex gap-3 items-center justify-between">
                  <InputField
                    id="password"
                    label="كلمة المرور"
                    type={showPassword ? "text" : "password"}
                    icon={Lock}
                    value={formData.password}
                    name="password"
                    onChange={handleChange}
                    placeholder="أدخل كلمة المرور"
                    required
                    error={errors.password}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`pr-11 w-full pl-12 py-3 text-xs border ${errors.password ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none`}
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute cursor-pointer left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </InputField>

                  <InputField
                    id="confirmPassword"
                    label="تأكيد كلمة المرور"
                    type={showPassword ? "text" : "password"}
                    icon={Lock}
                    value={formData.confirmPassword}
                    name="confirmPassword"
                    onChange={handleChange}
                    placeholder="أعد إدخال كلمة المرور"
                    required
                    error={errors.confirmPassword}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`pr-11 w-full pl-12 text-xs py-3 border ${errors.confirmPassword ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none`}
                      placeholder="أعد إدخال كلمة المرور"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute cursor-pointer left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </InputField>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">معلومات البائع</h2>

                <InputField
                  id="nameStore"
                  label="اسم المتجر (اختياري)"
                  icon={Store}
                  value={formData.nameStore}
                  name="nameStore"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder="أدخل اسم المتجر (إن وجد)"
                  error={errors.name_store}
                />

                <BankSelect 
                    type={true}
                    formData={formData} 
                    handleChange={handleChange} 
                    errors={errors} 
                  />

                <InputField
                  id="rib"
                  label="RIB (رقم الحساب البنكي)"
                  icon={Banknote}
                  value={formData.rib}
                  name="rib"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder="أدخل رقم الحساب البنكي (RIB)"
                  required
                  error={errors.rib}
                />

                <InputField
                  id="address"
                  label="العنوان"
                  icon={Home}
                  value={formData.address}
                  name="address"
                  className="text-xs"
                  onChange={handleChange}
                  placeholder="أدخل عنوانك الكامل"
                  required
                  error={errors.address}
                />

                <InputField
                  id="phone"
                  label="رقم الهاتف"
                  type="tel"
                  icon={Phone}
                  value={formData.phone}
                  name="phone"
                  onChange={handleChange}
                  placeholder="أدخل رقم هاتفك"
                  required
                  className="text-right text-xs"
                  error={errors.phone}
                />

                <div>
                  <label htmlFor="level" className="block text-xs font-semibold text-gray-700 mb-2">
                    مستوى البائع <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      className={`w-full text-xs cursor-pointer pr-4 pl-4 py-3 border ${errors.level ? 'border-red-400' : 'border-gray-200'} rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[var(--color-primary-300)] outline-none appearance-none`}
                      required
                    >
                      <option value="">اختر مستوى البائع</option>
                      <option value="Beginner">مبتدئ (Beginner)</option>
                      <option value="Intermediate">متوسط (Intermediate)</option>
                      <option value="Advanced">متقدم (Advanced)</option>
                    </select>
                    <ChevronDown size={15} className="absolute top-3.5 left-2 text-gray-500" />

                    {errors.level && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <Info className="w-3 h-3 ml-1 inline-block" /> {errors.level}
                      </p>
                    )}
                  </div>
                </div>

                <div className="">
                  <label htmlFor="logo" className="block text-xs font-semibold text-gray-700 mb-2">
                    شعار المتجر (اختياري)
                  </label>
                  <div className="relative border border-gray-200 rounded-xl bg-gray-50 focus-within:ring-2 focus-within:ring-[var(--color-primary-200)]">
                    <input
                      type="file"
                      id="logo"
                      name="logo"
                      onChange={handleLogoChange}
                      className="w-full cursor-pointer text-xs pr-11 pl-4 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[var(--color-primary-50)] file:text-[var(--color-primary-500)] hover:file:bg-[var(--color-primary-100)]"
                      accept="image/*"
                    />
                    <Image className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                  {errors.logo && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <Info className="w-3 h-3 ml-1 inline-block" /> {errors.logo}
                    </p>
                  )}

                  {logoPreview && (
                    <div className="mt-4 flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-gray-50">
                      <div className="flex items-center">
                        <img src={logoPreview} alt="Logo Preview" className="h-16 w-16 object-contain rounded-lg mr-4" />
                        <span className="text-sm text-gray-700">{logo ? logo.name : ''}</span>
                      </div>
                      <button
                        type="button"
                        onClick={clearLogo}
                        className="text-red-500 cursor-pointer hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition-colors"
                        aria-label="Remove logo"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between gap-4 mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="flex-1 text-xs cursor-pointer bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-300 focus:ring-4 focus:ring-gray-200 transition-all duration-200 flex items-center justify-center group"
                  disabled={loading}
                >
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                  العودة
                </button>
              )}

              {currentStep < 2 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className={`flex-1 cursor-pointer text-xs bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] text-white py-3 px-4 rounded-xl font-semibold hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)] focus:ring-4 focus:ring-blue-200 transition-all duration-200 flex items-center justify-center group shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin ml-2 w-4 h-4" /> : null}
                  التالي
                  <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                </button>
              ) : (
                <button
                  type="submit"
                  className={`flex-1 text-xs cursor-pointer bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] text-white py-3 px-4 rounded-xl font-semibold hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-400)] focus:ring-4 focus:ring-blue-200 transition-all duration-200 flex items-center justify-center group shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin ml-2 w-4 h-4" /> : null}
                  إنشاء الحساب
                  <ArrowLeft className="mr-2 w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-xs">
              لديك حساب بالفعل؟{" "}
              <Link to="/login" className="text-[var(--color-primary-500)] hover:text-[var(--color-primary-400)] font-semibold">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
